import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* --------------------------------------------
   FIXED COLOR SYSTEM
--------------------------------------------- */
const colorMap = {
  "text-indigo-600": "#6366f1",
  "text-emerald-600": "#059669",
  "text-rose-600": "#e11d48",
  "text-purple-600": "#7c3aed",
  "text-blue-600": "#2563eb",
  "text-orange-600": "#ea580c",
  "text-pink-600": "#db2777",
  "text-teal-600": "#0d9488",
};

/* --------------------------------------------
   CONSTANT CIRCLE VALUES
--------------------------------------------- */
const R = 22;
const C = 2 * Math.PI * R;
const strokeWidth = 6;

/* --------------------------------------------
   FIXED PIE CHART WITH LABEL
--------------------------------------------- */
const PieChart = ({ percent, color }) => (
  <div className="relative w-14 h-14 flex items-center justify-center">
    <svg className="w-full h-full rotate-[270deg]">
      <circle cx="28" cy="28" r={R} stroke="#e5e7eb" strokeWidth={strokeWidth} fill="none" />
      <motion.circle
        cx="28"
        cy="28"
        r={R}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={C}
        initial={{ strokeDashoffset: C }}
        animate={{ strokeDashoffset: C - (C * percent) / 100 }}
        transition={{ duration: 1 }}
        strokeLinecap="round"
      />
    </svg>

    <span className="absolute text-xs font-semibold" style={{ color }}>
      {percent}%
    </span>
  </div>
);

/* --------------------------------------------
   FIXED BAR GRAPH
--------------------------------------------- */
const BarGraph = ({ color, percent }) => {
  const bars = [
    percent * 0.2,
    percent * 0.45,
    percent * 0.35,
    percent * 0.6,
    percent * 0.3,
  ];

  return (
    <div className="flex items-end justify-center gap-1 h-14">
      {bars.map((h, i) => (
        <motion.div
          key={i}
          initial={{ height: 0 }}
          animate={{ height: (h / 100) * 48 }}
          transition={{ duration: 0.6, delay: i * 0.1 }}
          className="w-2 rounded-md"
          style={{ background: color }}
        />
      ))}
    </div>
  );
};

/* --------------------------------------------
   FIXED GAUGE
--------------------------------------------- */
const Gauge = ({ percent, color }) => (
  <div className="relative w-14 h-14 flex items-center justify-center">
    <svg className="w-full h-full rotate-[270deg]">
      <circle cx="28" cy="28" r={R} stroke="#e5e7eb" strokeWidth={strokeWidth} fill="none" />
      <motion.circle
        cx="28"
        cy="28"
        r={R}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={C}
        initial={{ strokeDashoffset: C }}
        animate={{ strokeDashoffset: C - (C * percent) / 100 }}
        transition={{ duration: 1 }}
        strokeLinecap="round"
      />
    </svg>

    <span className="absolute text-xs font-semibold" style={{ color }}>
      {percent}%
    </span>
  </div>
);

/* --------------------------------------------
   NUMBER WIDGET
--------------------------------------------- */
const NumberWidget = ({ number, suffix, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className="text-xl font-bold"
    style={{ color }}
  >
    {number}
    {suffix}
  </motion.div>
);

/* --------------------------------------------
   KPI LIST (unchanged)
--------------------------------------------- */
const KPI = [
  {
    title: "Employee Lifecycle Managed",
    number: 850,
    suffix: "+",
    desc: "Onboarding → Attendance → Payroll → Appraisal → Exit",
    color: "text-indigo-600",
    percent: 85,
  },
  {
    title: "Automated HR Processes",
    number: 12,
    suffix: "+",
    desc: "Attendance, Leave, Payroll, LMS, Performance, Recruitment",
    color: "text-emerald-600",
    percent: 92,
  },
  {
    title: "Paperless HR Efficiency",
    number: 100,
    suffix: "%",
    desc: "Digital onboarding, E-sign, policy automation, DOC vault",
    color: "text-rose-600",
    percent: 100,
  },
  {
    title: "Platform Reliability",
    number: 99.9,
    suffix: "%",
    desc: "High-availability cloud, encrypted data, stable payroll load",
    color: "text-purple-600",
    percent: 98,
  },
];

/* --------------------------------------------
   WIDGET AUTO ROTATION
--------------------------------------------- */
const widgetModes = ["pie", "bar", "gauge", "number"];

const StatsStrip = () => {
  const [mode, setMode] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setMode((m) => (m + 1) % widgetModes.length),
      2500
    );
    return () => clearInterval(id);
  }, []);

  return (
    <section
      className="py-24 bg-white"
      style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
    >
      <div className="max-w-7xl mx-auto px-6">

        {/* UPDATED PRO TITLE */}
        <h2
          className="text-center text-3xl font-bold text-gray-900"
          style={{ fontFamily: "Poppins, sans-serif" }}
        >
          Data-Driven HR Insights. Accurate. Actionable. Enterprise-Ready.
        </h2>

        {/* UPDATED PRO SUBTEXT */}
        <p
          className="text-center text-gray-600 mt-3 max-w-xl mx-auto text-sm"
          style={{ fontFamily: "Roboto, sans-serif" }}
        >
          Real-time intelligence from GTPL’s HRMS covering payroll cycles,
          workforce activity, attendance patterns, performance workflows
          and operational analytics.
        </p>

        {/* GRID */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {KPI.map((item, i) => {
            const color = colorMap[item.color];

            return (
              <div
                key={i}
                className="p-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
                style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
              >
                <div className="flex justify-center mb-4 h-14">
                  <AnimatePresence mode="wait">
                    {mode === 0 && <PieChart percent={item.percent} color={color} />}
                    {mode === 1 && <BarGraph percent={item.percent} color={color} />}
                    {mode === 2 && <Gauge percent={item.percent} color={color} />}
                    {mode === 3 && (
                      <NumberWidget
                        number={item.number}
                        suffix={item.suffix}
                        color={color}
                      />
                    )}
                  </AnimatePresence>
                </div>

                <h3 className={`font-semibold text-base ${item.color}`}>
                  {item.title}
                </h3>
                <p
                  className="text-gray-600 text-xs mt-2 leading-relaxed"
                  style={{ fontFamily: "Roboto, sans-serif" }}
                >
                  {item.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsStrip;
